import { CommonModule } from '@angular/common';
import { Component, computed, input, ViewEncapsulation } from '@angular/core';
import { IconKey } from '@common/classes/icons';
import { WarningRule } from '@common/directives/warning-validator.directive';
import { NgIconComponent } from '@ng-icons/core';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from '@progress/kendo-angular-tooltip';

/**
 * Configuration object for displaying a warning message.
 */
export interface WarningConfig {
    /** Translation key for the warning message */
    message: string;
    /** Severity level that affects icon and styling. Defaults to 'warning' */
    severity?: 'success' | 'warning' | 'error';
    /** Translation key for additional tooltip text */
    tooltip?: string;
}

/**
 * Component for displaying warning messages with animated icons and tooltips.
 * Supports two usage patterns:
 * 1. Direct warning object (backward compatibility)
 * 2. Value + validation rules (new approach)
 *
 * @example
 * ```html
 * <!-- Direct warning approach -->
 * <app-warning-display
 *   [warning]="warningObject"
 *   [showMessage]="true" />
 *
 * <!-- Value + rules approach -->
 * <app-warning-display
 *   [value]="model.lengthOverall"
 *   [rules]="vesselValidationRules.loa"
 *   [showMessage]="true" />
 * ```
 */
@Component({
    selector: 'app-warning-display',
    standalone: true,
    imports: [CommonModule, NgIconComponent, TooltipModule, TranslateModule],
    styleUrls: ['./warning-display.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        @if (displayWarning()) {
            <div
                class="inline-flex items-center gap-1"
                kendoTooltip
                [title]="displayWarning()!.tooltip || displayWarning()!.message | translate">
                <div class="warning-icon-container">
                    <ng-icon
                        class="warning-icon text-sm"
                        [name]="getIconName()"
                        [class.text-success]="displayWarning()!.severity === 'success'"
                        [class.text-warning]="displayWarning()!.severity === 'warning'"
                        [class.text-danger]="displayWarning()!.severity === 'error'"></ng-icon>
                </div>
                @if (showMessage()) {
                    <span
                        class="text-xs"
                        [class.text-success]="displayWarning()!.severity === 'success'"
                        [class.text-warning]="displayWarning()!.severity === 'warning'"
                        [class.text-danger]="displayWarning()!.severity === 'error'">
                        {{ displayWarning()!.message | translate }}
                    </span>
                }
            </div>
        }
    `
})
export class WarningDisplayComponent {
    /**
     * Direct warning configuration object.
     * Takes precedence over value + rules approach for backward compatibility.
     * @default null
     */
    warning = input<WarningConfig | null>();

    /**
     * Whether to display the warning message text alongside the icon.
     * When false, only the icon with tooltip is shown.
     * @default false
     */
    showMessage = input<boolean>(false);

    /**
     * The value to validate against the warning rules.
     * Used with the `rules` input for automatic warning computation.
     * Supports common validation types: numbers, strings, booleans, and null/undefined.
     * @default undefined
     */
    value = input<string | number | boolean | null | undefined>();

    /**
     * Array of warning validation rules to apply to the value.
     * The first matching rule will be displayed as a warning.
     * Used with the `value` input for automatic warning computation.
     * @default undefined
     */
    rules = input<WarningRule[]>();

    overridingMessage = input<string>();
    overridngTooltip = input<string>();

    /**
     * Computed warning based on value and rules.
     * Returns the direct warning if provided, otherwise evaluates rules against value.
     * @private
     */
    private computedWarning = computed(() => {
        // If warning is provided directly, use that (backward compatibility)
        if (this.warning()) return this.warning();

        const val = this.value();
        const ruleList = this.rules();
        if (val === null || val === undefined || !ruleList) return null;

        for (const rule of ruleList) {
            if (rule.condition(val)) {
                return {
                    message: this.overridingMessage() ?? rule.message,
                    severity: rule.severity || 'warning',
                    tooltip: this.overridngTooltip() ?? rule.tooltip
                } as WarningConfig;
            }
        }
        return null;
    });

    /**
     * The final warning to display in the template.
     * Combines both direct warning and computed warning approaches.
     */
    displayWarning = computed(() => this.computedWarning());

    /**
     * Gets the appropriate icon name based on the warning severity.
     * @returns Icon key for either error or warning icon
     */
    getIconName(): IconKey {
        return this.displayWarning()?.severity === 'error' ? 'faSolidCircleExclamation' : 'faSolidTriangleExclamation';
    }
}
